import { useMemo, useState } from "react";
import { Button, Card, Group, Progress, Stack, Text, Title } from "@mantine/core";
import { Link } from "react-router-dom";
import {
  openapi,
  useGetAuthMeQuery,
  useGetMultiplacerProjectsSingleProjectQuery,
} from "../../openapi";
import { useAppSelector } from "../app/hooks";
import { useProjectId } from "../hooks/useProjectId";
import { DrofusSyncJobButton } from "../DrofusForekomster/DrofusSyncJobButton";
import { DrofusScheduleButton } from "../DrofusForekomster/DrofusScheduleButton";
import { makeSelectRoomSyncAccess } from "./roomSyncSettingsSelectors";
import {
  makeSelectOccurrenceSyncRuns,
  occurrenceSyncRunsArgs,
} from "./occurrenceSyncSettingsSelectors";

export function OccurrenceSyncSettingsCard() {
  const projectNo = useProjectId();
  const hasProject = Number.isFinite(projectNo) && projectNo > 0;
  const [pending, setPending] = useState(false);
  const selectAccess = useMemo(() => makeSelectRoomSyncAccess(projectNo), [projectNo]);
  const selectRuns = useMemo(() => makeSelectOccurrenceSyncRuns(projectNo), [projectNo]);
  const { canStart } = useAppSelector(selectAccess);
  const { running, latest } = useAppSelector(selectRuns);
  useGetAuthMeQuery();
  useGetMultiplacerProjectsSingleProjectQuery({ projectNo }, { skip: !hasProject });
  const { isLoading, isError, refetch } =
    openapi.endpoints.getMultiplacerDrofusInterfaceRuns.useQuery(
      occurrenceSyncRunsArgs(projectNo),
      {
        skip: !hasProject,
        refetchOnMountOrArgChange: true,
        pollingInterval: running || pending ? 10000 : 0,
      }
    );

  return (
    <Card withBorder radius="lg" p="lg">
      <Stack gap="md">
        <Title order={4}>Forekomst- og artikkelsynk</Title>
        <Text size="sm" c="dimmed">
          Hent forekomster og artikler fra prosjektets dRofus-integrasjon.
        </Text>
        <Text size="sm">
          Kun prosjekteiere kan starte eller planlegge synk. Prosjekteierne er listet under romsynk.
        </Text>
        {canStart && (
          <Group gap="xs">
            <DrofusSyncJobButton
              projectNo={projectNo}
              disabled={!hasProject || isLoading || isError}
              runningRunId={running?._id ?? null}
              onStarted={() => void refetch()}
              onPendingChange={setPending}
            />
            <DrofusScheduleButton projectNo={projectNo} disabled={!hasProject} />
          </Group>
        )}
        {isError ? (
          <Group>
            <Text size="sm" c="red">
              Kunne ikke hente kjøringer.
            </Text>
            <Button variant="light" size="xs" onClick={() => void refetch()}>
              Prøv igjen
            </Button>
          </Group>
        ) : isLoading ? (
          <Text size="sm" c="dimmed">
            Laster kjøringer…
          </Text>
        ) : running || pending ? (
          <Stack gap="xs">
            <Text size="sm">
              {pending
                ? "Starter jobb…"
                : running?.status === "queued"
                  ? "dRofus-jobb i kø"
                  : "dRofus-jobb kjører"}
            </Text>
            <Progress value={100} animated striped />
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            {latest
              ? `Siste kjøring: ${latest.status} · ${new Date(latest.startedAt).toLocaleString("nb-NO")}`
              : "Ingen tidligere kjøringer."}
          </Text>
        )}
        <Button
          component={Link}
          to={`/${projectNo}/forekomster?module=CEILING`}
          variant="light"
          size="xs"
          style={{ alignSelf: "flex-start" }}
        >
          Vis forekomster og kjørehistorikk
        </Button>
      </Stack>
    </Card>
  );
}
